import React, { useState, useEffect } from 'react';
import { Bell, Plus, X, Clock, Check, Calendar, Star, Filter, Search, ChevronRight } from 'lucide-react';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import TabNavigation from '../components/TabNavigation';
import BottomNavigation from '../components/BottomNavigation';
import { t } from '../i18n';

/**
 * Bildirimler Ekranı
 * Kullanıcının bildirimlerini yönettiği ve görüntülediği ekran
 */
const NotificationsScreen = () => {
  const { navigateToScreen } = useUI();
  const { user } = useAuth();
  
  // UI durumları
  const [activeTab, setActiveTab] = useState('incoming');
  const [showAddNotification, setShowAddNotification] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [reminders, setReminders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Sekme öğeleri
  const tabItems = [
    { id: 'incoming', label: t('notifications.tabs.incoming', 'Gelen') },
    { id: 'reminders', label: t('notifications.tabs.reminders', 'Hatırlatıcılar') },
    { id: 'tasks', label: t('notifications.tabs.tasks', 'Görevler') }
  ];

  // Demo verileri
  useEffect(() => {
    // İstek simülasyonu
    const loadData = async () => {
      try {
        setIsLoading(true);
        // API isteği simülasyonu
        await new Promise(resolve => setTimeout(resolve, 600));
        
        // Gelen bildirimler
        const demoNotifications = [
          {
            id: 1,
            type: 'connection',
            sender: 'Ahmet Yılmaz',
            content: t('notifications.demo.connectionRequest', 'Bağlantı isteği gönderdi'),
            timestamp: Date.now() - 1800000, // 30 dakika önce
            isRead: false,
            profileImage: null
          },
          {
            id: 2,
            type: 'verification',
            sender: t('notifications.demo.idappsVerification', 'IDapps Doğrulama'),
            content: t('notifications.demo.qrVerified', 'QR kodunuz başarıyla doğrulandı'),
            timestamp: Date.now() - 86400000, // 1 gün önce
            isRead: true,
            profileImage: null
          },
          {
            id: 3,
            type: 'security',
            sender: t('notifications.demo.securityCenter', 'Güvenlik Merkezi'),
            content: t('notifications.demo.newLogin', 'Hesabınıza yeni bir cihazdan giriş yapıldı'),
            timestamp: Date.now() - 172800000, // 2 gün önce
            isRead: false,
            profileImage: null
          },
          {
            id: 4,
            type: 'system',
            sender: t('notifications.demo.system', 'Sistem'),
            content: t('notifications.demo.appUpdated', 'IDapps uygulaması güncellendi, yeni özellikleri keşfedin'),
            timestamp: Date.now() - 259200000, // 3 gün önce
            isRead: true,
            profileImage: null
          },
          {
            id: 5,
            type: 'message',
            sender: 'Merve Demir',
            content: t('notifications.demo.sentMessage', 'Size bir mesaj gönderdi: "Merhaba, nasılsın?"'),
            timestamp: Date.now() - 345600000, // 4 gün önce
            isRead: true,
            profileImage: null
          },
          {
            id: 6,
            type: 'game',            sender: t('notifications.demo.identityCircle', 'IDapps IdentityCircle'),
            content: t('notifications.demo.newConnection', 'Yeni güvenilir bağlantı daveti alındı! QR doğrulama ile güvenli network genişletme.'),
            timestamp: Date.now() - 86400000, // 1 gün önce
            isRead: false,
            profileImage: null
          },
          {
            id: 7,
            type: 'connection',
            sender: 'Fatma Öztürk',
            content: t('notifications.demo.connectionAccepted', 'Bağlantı isteğinizi kabul etti'),
            timestamp: Date.now() - 7200000, // 2 saat önce
            isRead: true,
            profileImage: null
          }
        ];
        
        // Hatırlatıcılar
        const demoReminders = [
          {
            id: 101,
            title: t('notifications.demo.reminders.weeklyMeeting', 'Haftalık Toplantı'),
            description: t('notifications.demo.reminders.teamMeeting', 'Ekip toplantısı - Zoom üzerinden'),
            timestamp: Date.now() + 3600000, // 1 saat sonra
            priority: 'high',
            isCompleted: false,
            repeat: 'weekly',
            category: 'work'
          },
          {
            id: 102,
            title: t('notifications.demo.reminders.doctorAppointment', 'Doktor Randevusu'),
            description: t('notifications.demo.reminders.dentalCheckup', 'Diş kontrolü - Ankara Diş Hastanesi'),
            timestamp: Date.now() + 86400000, // 1 gün sonra
            priority: 'medium',
            isCompleted: false,
            repeat: 'none',
            category: 'health'
          },
          {
            id: 103,
            title: t('notifications.demo.reminders.bills', 'Faturalar'),
            description: t('notifications.demo.reminders.utilityBills', 'Elektrik ve su faturası ödemesi'),
            timestamp: Date.now() + 172800000, // 2 gün sonra
            priority: 'low',
            isCompleted: false,
            repeat: 'monthly',
            category: 'finance'
          },
          {
            id: 104,
            title: t('notifications.demo.reminders.birthday', 'Doğum Günü'),
            description: t('notifications.demo.reminders.mothersBirthday', 'Annemin doğum günü - Hediye al'),
            timestamp: Date.now() + 518400000, // 6 gün sonra
            priority: 'medium',
            isCompleted: false,
            repeat: 'yearly',
            category: 'personal'
          },
          {
            id: 105,            title: t('notifications.demo.reminders.identityVerification', 'Kimlik Doğrulama'),
            description: t('notifications.demo.reminders.networkExpansion', 'IDapps IdentityCircle güvenli network genişletme'),
            timestamp: Date.now() + 259200000, // 3 gün sonra
            priority: 'medium',
            isCompleted: false,
            repeat: 'none',
            category: 'entertainment'
          }
        ];
        
        setNotifications(demoNotifications);
        setReminders(demoReminders);
        setIsLoading(false);
      } catch (error) {
        console.error(t('notifications.errors.loadFailed', "Bildirim verilerini yüklerken hata:"), error);
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Bildirimleri işaretle
  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, isRead: true } 
          : notification
      )
    );
  };

  // Bildirimi sil
  const deleteNotification = (notificationId) => {
    setNotifications(prev => 
      prev.filter(notification => notification.id !== notificationId)
    );
  };

  // Tüm bildirimleri okundu olarak işaretle
  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, isRead: true }))
    );
  };

  // Bildirim türüne göre simge
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'connection':
        return <svg className="text-blue-500" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
      case 'verification':
        return <svg className="text-green-500" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>;
      case 'security':
        return <svg className="text-red-500" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
      case 'message':
        return <svg className="text-indigo-500" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>;
      case 'game':
        return <svg className="text-purple-500" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="11" width="4" height="4" rx="1" /><rect x="14" y="11" width="4" height="4" rx="1" /><rect x="10" y="5" width="4" height="4" rx="1" /><path d="M17.4 2.6a3 3 0 0 0-4.8 0L12 3l-.6-.4a3 3 0 0 0-4.8 0" /><rect x="10" y="17" width="4" height="4" rx="1" /></svg>;
      default:
        return <svg className="text-gray-500" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>;
    }
  };

  // Hatırlatıcıyı tamamla
  const toggleReminderComplete = (reminderId) => {
    setReminders(prev => 
      prev.map(reminder => 
        reminder.id === reminderId 
          ? { ...reminder, isCompleted: !reminder.isCompleted } 
          : reminder
      )
    );
  };

  // Hatırlatıcıyı sil
  const deleteReminder = (reminderId) => {
    setReminders(prev => 
      prev.filter(reminder => reminder.id !== reminderId)
    );
  };

  // Yeni hatırlatıcı ekle
  const addNewReminder = (reminderData) => {
    const newReminder = {
      id: Date.now(),
      ...reminderData,
      isCompleted: false
    };
    
    setReminders(prev => [...prev, newReminder]);
    setShowAddNotification(false);
  };

  // Zaman formatı
  const formatTime = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    
    if (diff < 0) {
      // Gelecek zaman
      const futureDate = new Date(timestamp);
      
      // Bugün içerisinde ise
      if (futureDate.toDateString() === new Date().toDateString()) {
        return t('notifications.time.today', 'Bugün {time}', { 
          time: `${futureDate.getHours()}:${String(futureDate.getMinutes()).padStart(2, '0')}` 
        });
      }
      
      // Yarın ise
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (futureDate.toDateString() === tomorrow.toDateString()) {
        return t('notifications.time.tomorrow', 'Yarın {time}', { 
          time: `${futureDate.getHours()}:${String(futureDate.getMinutes()).padStart(2, '0')}` 
        });
      }
      
      // Diğer günler
      return t('notifications.time.futureDate', '{date} {time}', {
        date: `${futureDate.getDate()}/${futureDate.getMonth() + 1}`,
        time: `${futureDate.getHours()}:${String(futureDate.getMinutes()).padStart(2, '0')}`
      });
    }
    
    // Geçmiş zaman
    if (diff < 60000) return t('notifications.time.justNow', 'Az önce');
    if (diff < 3600000) return t('notifications.time.minutesAgo', '{minutes} dakika önce', { minutes: Math.floor(diff / 60000) });
    if (diff < 86400000) return t('notifications.time.hoursAgo', '{hours} saat önce', { hours: Math.floor(diff / 3600000) });
    if (diff < 604800000) return t('notifications.time.daysAgo', '{days} gün önce', { days: Math.floor(diff / 86400000) });
    
    const date = new Date(timestamp);
    return t('notifications.time.fullDate', '{day}/{month}/{year}', {
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear()
    });
  };

  // Öncelik rengi
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  // Kategori simgesi
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'work':
        return <svg className="text-blue-600" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>;
      case 'health':
        return <svg className="text-red-600" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>;
      case 'finance':
        return <svg className="text-green-600" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>;
      case 'entertainment':
        return <svg className="text-purple-600" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>;
      default:
        return <svg className="text-gray-600" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>;
    }
  };

  // Tekrarlama simgesi
  const getRepeatIcon = (repeat) => {
    switch (repeat) {
      case 'daily': return t('notifications.repeat.daily', '🔄 Her gün');
      case 'weekly': return t('notifications.repeat.weekly', '🔄 Her hafta');
      case 'monthly': return t('notifications.repeat.monthly', '🔄 Her ay');
      case 'yearly': return t('notifications.repeat.yearly', '🔄 Her yıl');
      default: return null;
    }
  };

  // Filtreleme işlemi
  const filterNotifications = () => {
    if (activeTab === 'incoming') {
      let filtered = [...notifications];
      
      // Arama filtresi
      if (searchQuery) {
        filtered = filtered.filter(notification => 
          notification.sender.toLowerCase().includes(searchQuery.toLowerCase()) ||
          notification.content.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      
      // Tip filtresi
      if (selectedFilter !== 'all') {
        filtered = filtered.filter(notification => notification.type === selectedFilter);
      }
      
      return filtered;
    } else if (activeTab === 'reminders') {
      let filtered = [...reminders];
      
      // Arama filtresi
      if (searchQuery) {
        filtered = filtered.filter(reminder => 
          reminder.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          reminder.description.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      
      // Tamamlanma filtresi
      if (selectedFilter === 'completed') {
        filtered = filtered.filter(reminder => reminder.isCompleted);
      } else if (selectedFilter === 'active') {
        filtered = filtered.filter(reminder => !reminder.isCompleted);
      }
      
      // Öncelik filtresi
      if (['high', 'medium', 'low'].includes(selectedFilter)) {
        filtered = filtered.filter(reminder => reminder.priority === selectedFilter);
      }
      
      return filtered;
    }
    
    return [];
  };

  // Okunmamış bildirimleri sayma
  const getUnreadCount = () => {
    return notifications.filter(n => !n.isRead).length;
  };

  // Yeni Hatırlatıcı Ekleme Formu
  const ReminderForm = () => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [priority, setPriority] = useState('medium');
    const [repeat, setRepeat] = useState('none');
    const [category, setCategory] = useState('personal');
    
    // Form gönderimi sırasında bugün ve şimdi değerlerini varsayılan olarak ayarla
    useEffect(() => {
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0];
      setDate(formattedDate);
      
      // Şimdiki saat ve dakika
      const hours = String(today.getHours()).padStart(2, '0');
      const minutes = String(today.getMinutes()).padStart(2, '0');
      setTime(`${hours}:${minutes}`);
    }, []);
    
    const handleSubmit = (e) => {
      e.preventDefault();
      
      // Tarih ve saat birleştirme
      const dateTime = new Date(`${date}T${time || '00:00'}`);
      
      addNewReminder({
        title,
        description,
        timestamp: dateTime.getTime(),
        priority,
        repeat,
        category
      });
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-lg w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-bold">{t('notifications.reminder.new', 'Yeni Hatırlatıcı')}</h2>
            <button 
              onClick={() => setShowAddNotification(false)}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <X size={20} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('notifications.reminder.title', 'Başlık')}</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg"
                placeholder={t('notifications.reminder.titlePlaceholder', 'Hatırlatıcı başlığı')}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('notifications.reminder.description', 'Açıklama')}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg"
                placeholder={t('notifications.reminder.descriptionPlaceholder', 'Açıklama (isteğe bağlı)')}
                rows={2}
              />
            </div>
            
            <div className="flex space-x-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('notifications.reminder.date', 'Tarih')}</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('notifications.reminder.time', 'Saat')}</label>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('notifications.reminder.category', 'Kategori')}</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg appearance-none"
              >
                <option value="personal">{t('notifications.categories.personal', 'Kişisel')}</option>
                <option value="work">{t('notifications.categories.work', 'İş')}</option>
                <option value="health">{t('notifications.categories.health', 'Sağlık')}</option>
                <option value="finance">{t('notifications.categories.finance', 'Finans')}</option>
                <option value="entertainment">{t('notifications.categories.entertainment', 'Eğlence')}</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('notifications.reminder.priority', 'Öncelik')}</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg appearance-none"
              >
                <option value="low">{t('notifications.priorities.low', 'Düşük')}</option>
                <option value="medium">{t('notifications.priorities.medium', 'Orta')}</option>
                <option value="high">{t('notifications.priorities.high', 'Yüksek')}</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('notifications.reminder.repeat', 'Tekrarlama')}</label>
              <select
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg appearance-none"
              >
                <option value="none">{t('notifications.reminder.noRepeat', 'Tekrar yok')}</option>
                <option value="daily">{t('notifications.repeat.daily', 'Her gün')}</option>
                <option value="weekly">{t('notifications.repeat.weekly', 'Her hafta')}</option>
                <option value="monthly">{t('notifications.repeat.monthly', 'Her ay')}</option>
                <option value="yearly">{t('notifications.repeat.yearly', 'Her yıl')}</option>
              </select>
            </div>
            
            <div className="flex space-x-2 pt-2">
              <button
                type="button"
                onClick={() => setShowAddNotification(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg"
              >
                {t('common.cancel', 'İptal')}
              </button>
              <button
                type="submit"
                className="flex-1 py-2 bg-indigo-600 text-white rounded-lg"
              >
                {t('common.add', 'Ekle')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Filtre Seçenekleri
  const FilterOptions = () => {
    const options = activeTab === 'incoming'
      ? [
          { id: 'all', label: t('notifications.filters.all', 'Tümü') },
          { id: 'connection', label: t('notifications.filters.connections', 'Bağlantılar') },
          { id: 'message', label: t('notifications.filters.messages', 'Mesajlar') },
          { id: 'verification', label: t('notifications.filters.verifications', 'Doğrulamalar') },
          { id: 'security', label: t('notifications.filters.security', 'Güvenlik') },
          { id: 'game', label: t('notifications.filters.games', 'Oyunlar') },
          { id: 'system', label: t('notifications.filters.system', 'Sistem') }
        ]
      : [
          { id: 'all', label: t('notifications.filters.all', 'Tümü') },
          { id: 'active', label: t('notifications.filters.active', 'Aktif') },
          { id: 'completed', label: t('notifications.filters.completed', 'Tamamlanan') },
          { id: 'high', label: t('notifications.filters.highPriority', 'Yüksek Öncelik') },
          { id: 'medium', label: t('notifications.filters.mediumPriority', 'Orta Öncelik') },
          { id: 'low', label: t('notifications.filters.lowPriority', 'Düşük Öncelik') }
        ];
    
    return (
      <div className="absolute top-full left-0 right-0 bg-white shadow-lg rounded-b-lg z-20 p-2">
        <div className="grid grid-cols-2 gap-2">
          {options.map(option => (
            <button
              key={option.id}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                selectedFilter === option.id 
                  ? 'bg-indigo-100 text-indigo-700' 
                  : 'bg-gray-100 text-gray-700'
              }`}
              onClick={() => {
                setSelectedFilter(option.id);
                setShowFilterOptions(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Yükleme ekranı
  if (isLoading) {
    return (
      <div className="flex flex-col h-screen bg-gray-100">
        <header className="bg-white p-4 shadow z-10">
          <h1 className="font-bold text-lg">{t('screens.notificationsTitle', 'Bildirimler')}</h1>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">{t('notifications.loading', 'Bildirimler yükleniyor...')}</p>
          </div>
        </div>
        <BottomNavigation activeItem="notifications" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white p-4 shadow z-10">
        <div className="flex justify-between items-center mb-4">
          <h1 className="font-bold text-lg">{t('screens.notificationsTitle', 'Bildirimler')}</h1>
          <div className="flex items-center">
            {activeTab === 'incoming' && getUnreadCount() > 0 && (
              <button 
                className="mr-2 text-sm text-indigo-600 font-medium"
                onClick={markAllAsRead}
              >
                {t('notifications.markAllAsRead', 'Tümünü Okundu İşaretle')}
              </button>
            )}
            {activeTab === 'reminders' && (
              <button 
                className="p-2 bg-indigo-600 text-white rounded-full"
                onClick={() => setShowAddNotification(true)}
              >
                <Plus size={20} />
              </button>
            )}
          </div>
        </div>
        
        {/* Arama ve Filtre */}
        <div className="relative flex items-center space-x-2">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('common.search', 'Ara...')}
              className="w-full p-2 pl-10 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="relative">
            <button 
              className={`p-2 rounded-full ${showFilterOptions ? 'bg-indigo-100' : 'bg-gray-100'}`}
              onClick={() => setShowFilterOptions(!showFilterOptions)}
            >
              <Filter size={20} className={showFilterOptions ? 'text-indigo-600' : 'text-gray-600'} />
            </button>
            {showFilterOptions && <FilterOptions />}
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <TabNavigation 
        items={tabItems}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Main Content */}
      <main className="flex-1 overflow-auto pb-navigation">
        {activeTab === 'incoming' && (
          <div className="divide-y">
            {filterNotifications().length > 0 ? (
              filterNotifications().map(notification => (
                <div 
                  key={notification.id} 
                  className={`flex p-4 ${notification.isRead ? 'bg-white' : 'bg-blue-50'}`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <div className="mr-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                      {notification.profileImage ? (
                        <img 
                          src={notification.profileImage} 
                          alt={notification.sender} 
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex items-center justify-center">
                          {getNotificationIcon(notification.type)}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <h3 className="font-medium">
                        {notification.sender}
                        {!notification.isRead && (
                          <span className="ml-2 bg-indigo-600 text-white text-xs px-1.5 py-0.5 rounded-full">{t('notifications.new', 'Yeni')}</span>
                        )}
                      </h3>
                      <span className="text-xs text-gray-500">{formatTime(notification.timestamp)}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{notification.content}</p>
                      {/* IdentityCircle bildirimi için özel buton */}
                    {notification.type === 'game' && !notification.isRead && (
                      <button
                        className="mt-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded"
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification.id);
                          navigateToScreen('identityCircle');
                        }}
                      >
                        {t('notifications.goToIdentityCircle', 'IdentityCircle\'a Git')}
                      </button>
                    )}
                  </div>
                  
                  <button
                    className="ml-2 p-1 text-gray-400 hover:text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(notification.id);
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))
            ) : (
              <div className="p-8 text-center">
                <Bell size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">
                  {searchQuery 
                    ? t('notifications.noSearchResults', 'Arama sonucu bulunamadı') 
                    : t('notifications.noNotifications', 'Henüz bildirim yok')
                  }
                </p>
                {searchQuery && (
                  <button 
                    className="mt-4 px-4 py-2 bg-gray-200 rounded-lg text-gray-700"
                    onClick={() => setSearchQuery('')}
                  >
                    {t('notifications.clearSearch', 'Aramayı Temizle')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'reminders' && (
          <div className="divide-y">
            {filterNotifications().length > 0 ? (
              filterNotifications().map(reminder => (
                <div 
                  key={reminder.id} 
                  className={`p-4 bg-white ${reminder.isCompleted ? 'opacity-70' : ''}`}
                >
                  <div className="flex items-start">
                    <button
                      className={`mr-3 p-1 rounded-full border ${
                        reminder.isCompleted 
                          ? 'bg-green-100 border-green-500 text-green-500' 
                          : 'border-gray-300'
                      }`}
                      onClick={() => toggleReminderComplete(reminder.id)}
                    >
                      {reminder.isCompleted ? (
                        <Check size={16} />
                      ) : (
                        <div className="w-4 h-4"></div>
                      )}
                    </button>
                    
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className={`font-medium ${reminder.isCompleted ? 'line-through text-gray-500' : ''}`}>
                            {reminder.title}
                          </h3>
                          <div className="flex items-center mt-1">
                            <span className={`inline-block w-2 h-2 rounded-full mr-1 ${getPriorityColor(reminder.priority)}`}></span>
                            <span className="text-xs text-gray-500 mr-2">{formatTime(reminder.timestamp)}</span>
                            <span className="text-xs flex items-center">
                              {getCategoryIcon(reminder.category)}
                              <span className="ml-1">{t(`notifications.categories.${reminder.category}`, reminder.category)}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600 mt-1">
                        {reminder.description}
                      </p>
                      
                      {getRepeatIcon(reminder.repeat) && (
                        <div className="mt-2 text-xs text-gray-500">
                          {getRepeatIcon(reminder.repeat)}
                        </div>
                      )}
                    </div>
                    
                    <button
                      className="ml-2 p-1 text-gray-400 hover:text-gray-600"
                      onClick={() => deleteReminder(reminder.id)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center">
                <Clock size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">
                  {searchQuery 
                    ? t('notifications.noSearchResults', 'Arama sonucu bulunamadı') 
                    : t('notifications.noReminders', 'Henüz hatırlatıcı yok')
                  }
                </p>
                <button
                  className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg"
                  onClick={() => setShowAddNotification(true)}
                >
                  {t('notifications.addReminder', 'Hatırlatıcı Ekle')}
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="p-4 space-y-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold">{t('notifications.tasks.dailyTasks', 'Günlük Görevler')}</h3>
                <ChevronRight size={18} className="text-gray-400" />
              </div>
              <div className="mt-3 space-y-2">
                <div className="flex items-center">
                  <Check size={16} className="text-green-500 mr-2" />
                  <span>{t('notifications.tasks.wakeUp', 'Sabah 7\'de uyan')}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 border border-gray-300 rounded-sm mr-2"></div>
                  <span>{t('notifications.tasks.checkEmails', 'E-postaları kontrol et')}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 border border-gray-300 rounded-sm mr-2"></div>
                  <span>{t('notifications.tasks.reviewProject', 'Proje planını gözden geçir')}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 border border-gray-300 rounded-sm mr-2"></div>
                  <span>{t('notifications.tasks.completeNetworkTasks', 'IDapps IdentityCircle\'da güvenli bağlantıları kontrol et')}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold">{t('notifications.tasks.workSchedule', 'Çalışma Programı')}</h3>
                <ChevronRight size={18} className="text-gray-400" />
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-sm text-gray-500">
                  <span>09:00 - 12:00</span>
                  <span>{t('notifications.tasks.projectDevelopment', 'Proje geliştirme')}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 mt-1">
                  <span>13:00 - 15:00</span>
                  <span>{t('notifications.tasks.meetings', 'Toplantılar')}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 mt-1">
                  <span>15:30 - 18:00</span>
                  <span>{t('notifications.tasks.codeReviews', 'Kod incelemeleri')}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 mt-1">
                  <span>19:00 - 20:00</span>
                  <span>{t('notifications.tasks.identityVerification', 'Kimlik Doğrulama Network')}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold">{t('notifications.tasks.upcomingEvents', 'Yaklaşan Etkinlikler')}</h3>
                <Calendar size={18} className="text-gray-400" />
              </div>
              <div className="mt-3 space-y-3">
                <div>
                  <div className="flex justify-between">
                    <span className="font-medium">{t('notifications.tasks.blockchainMeetup', 'Blockchain Meetup')}</span>
                    <span className="text-xs text-gray-500">18 Mart</span>
                  </div>
                  <p className="text-sm text-gray-600">ATO Congresium, Ankara</p>
                </div>
                <div>
                  <div className="flex justify-between">
                    <span className="font-medium">{t('notifications.tasks.web3Conference', 'Web3 Konferansı')}</span>
                    <span className="text-xs text-gray-500">2 Nisan</span>
                  </div>
                  <p className="text-sm text-gray-600">{t('notifications.tasks.onlineEvent', 'Online Etkinlik')}</p>
                </div>
                <div>
                  <div className="flex justify-between">
                    <span className="font-medium">{t('notifications.tasks.idappsGameTournament', 'IDapps Oyun Turnuvası')}</span>
                    <span className="text-xs text-gray-500">12 Mart</span>
                  </div>
                  <p className="text-sm text-gray-600">{t('notifications.tasks.metaverseEvent', 'Metaverse Etkinliği')}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold">{t('notifications.tasks.goals', 'Hedefler')}</h3>
                <Star size={18} className="text-yellow-500" />
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">{t('notifications.tasks.dailyStepGoal', 'Günlük adım hedefi')}</span>
                    <span className="text-xs text-indigo-600">8,450 / 10,000</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 rounded-full" style={{ width: '84.5%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">{t('notifications.tasks.waterIntake', 'Su içme (ml)')}</span>
                    <span className="text-xs text-indigo-600">1,200 / 2,000</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full" style={{ width: '60%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">{t('notifications.tasks.workTime', 'Çalışma süresi')}</span>
                    <span className="text-xs text-indigo-600">4.5 / 8 {t('notifications.tasks.hours', 'saat')}</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full" style={{ width: '56.25%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">{t('notifications.tasks.idcoinGoal', 'IDCOIN Hedefi')}</span>
                    <span className="text-xs text-indigo-600">85 / 100</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full" style={{ width: '85%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation activeItem="notifications" />
      
      {/* Yeni Hatırlatıcı Modal */}
      {showAddNotification && <ReminderForm />}
    </div>
  );
};

export default NotificationsScreen;